import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, AlertDescription, Button, Card, CardContent, CardHeader, CardTitle } from '@cartcloser/ui';
import { acceptInvitation } from '../lib/api.js';

export const Route = createFileRoute('/invite/accept')({
  validateSearch: (search: Record<string, unknown>): { token: string } => ({
    token: typeof search.token === 'string' ? search.token : '',
  }),
  component: AcceptInvitePage,
});

function AcceptInvitePage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'working' | 'error'>('working');
  const [message, setMessage] = useState('Accepting your invitation…');

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!token) {
        setStatus('error');
        setMessage('This invitation link is missing its token.');
        return;
      }
      try {
        const result = await acceptInvitation(token);
        if (cancelled) return;
        // Refresh memberships so the dashboard sees the new store.
        await queryClient.invalidateQueries({ queryKey: ['me'] });
        if (result.slug) {
          void navigate({ to: '/admin/$slug', params: { slug: result.slug } });
        } else {
          void navigate({ to: '/app' });
        }
      } catch (e) {
        if (cancelled) return;
        setStatus('error');
        const reason = e instanceof Error ? e.message : 'unknown';
        setMessage(
          reason === 'email_mismatch'
            ? 'This invitation was sent to a different email. Sign in with that address to accept.'
            : reason === 'expired'
              ? 'This invitation has expired. Ask for a new one.'
              : reason === 'unauthorized'
                ? 'Please sign in first, then open the invitation link again.'
                : 'This invitation could not be accepted. It may have already been used.',
        );
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [token, navigate, queryClient]);

  return (
    <main className="mx-auto max-w-md px-6 py-20">
      <Card>
        <CardHeader>
          <CardTitle>Team invitation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'working' ? (
            <p className="text-sm text-muted-foreground">{message}</p>
          ) : (
            <>
              <Alert variant="destructive">
                <AlertDescription>{message}</AlertDescription>
              </Alert>
              <Button variant="outline" onClick={() => void navigate({ to: '/signin' })}>
                Go to sign in
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
